import { useMemo, useState } from "react";
import {
  DEFAULT_HERO_BUDGETS,
  METRIC_LABELS,
  loadHeroBudgets,
  resetHeroBudgets,
  saveHeroBudgets,
  type BudgetMetric,
  type HeroBudgets,
} from "@/lib/hero-perf-budgets";
import { p75, type Breakpoint, type HeroPerfSample } from "@/lib/hero-perf-samples";

const BREAKPOINTS: Breakpoint[] = ["mobile", "tablet", "desktop"];
const METRICS = Object.keys(METRIC_LABELS) as BudgetMetric[];

/**
 * Budget editor for /qa/hero/perf. Values persist to localStorage and are read
 * by the live monitor (console warnings + on-page banner) on every page load.
 */
export const HeroBudgetEditor = ({
  samples,
}: {
  samples: HeroPerfSample[];
}) => {
  const [budgets, setBudgets] = useState<HeroBudgets>(() => loadHeroBudgets());
  const [saved, setSaved] = useState(false);

  const p75ByBreakpoint = useMemo(() => {
    const out = {} as Record<Breakpoint, Partial<Record<BudgetMetric, number>>>;
    BREAKPOINTS.forEach((bp) => {
      const rows = samples.filter((s) => s.breakpoint === bp);
      out[bp] = {
        lcpMs: p75(rows, "lcpMs"),
        decodeMs: p75(rows, "decodeMs"),
        totalMs: p75(rows, "totalMs"),
      };
    });
    return out;
  }, [samples]);

  const update = (bp: Breakpoint, metric: BudgetMetric, value: number) => {
    setSaved(false);
    setBudgets((prev) => ({
      ...prev,
      [bp]: { ...prev[bp], [metric]: Math.max(0, value) },
    }));
  };

  const persist = () => {
    saveHeroBudgets(budgets);
    setSaved(true);
  };

  const reset = () => {
    resetHeroBudgets();
    setBudgets(loadHeroBudgets());
    setSaved(true);
  };

  return (
    <section className="rounded border border-border/60 bg-navy-elevated/40 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-sm uppercase tracking-[0.2em] text-gold">
            Performance budgets
          </h2>
          <p className="mt-1 text-xs text-silver/70">
            Breaches log <span className="font-mono">[hero-budget]</span> console
            warnings and raise an on-page banner (dev, or any page with{" "}
            <span className="font-mono">?heroBudget=1</span>). Defaults:{" "}
            {DEFAULT_HERO_BUDGETS.mobile.lcpMs}ms LCP mobile /{" "}
            {DEFAULT_HERO_BUDGETS.desktop.lcpMs}ms desktop.
          </p>
        </div>
        <div className="flex items-center gap-2 text-xs">
          {saved && <span className="text-emerald">Saved</span>}
          <button
            type="button"
            onClick={persist}
            className="rounded border border-emerald/60 px-3 py-1.5 text-emerald hover:bg-emerald/10"
          >
            Save budgets
          </button>
          <button
            type="button"
            onClick={reset}
            className="rounded border border-border px-3 py-1.5 text-silver/80 hover:bg-white/5"
          >
            Reset defaults
          </button>
        </div>
      </div>

      <div className="mt-4 overflow-x-auto">
        <table className="w-full min-w-[640px] text-xs">
          <thead className="text-silver/60">
            <tr>
              <th className="p-2 text-left font-normal">Breakpoint</th>
              {METRICS.map((m) => (
                <th key={m} className="p-2 text-left font-normal">
                  {METRIC_LABELS[m]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {BREAKPOINTS.map((bp) => (
              <tr key={bp} className="border-t border-border/40">
                <td className="p-2 capitalize text-silver/90">{bp}</td>
                {METRICS.map((m) => {
                  const actual = p75ByBreakpoint[bp]?.[m];
                  const over = actual != null && actual > budgets[bp][m];
                  return (
                    <td key={m} className="p-2">
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min={0}
                          step={10}
                          value={budgets[bp][m]}
                          onChange={(e) =>
                            update(bp, m, Number(e.target.value) || 0)
                          }
                          aria-label={`${bp} ${METRIC_LABELS[m]} budget in milliseconds`}
                          className="w-24 rounded border border-border bg-transparent px-2 py-1 tabular-nums"
                        />
                        <span
                          className={
                            over
                              ? "text-rose-300 tabular-nums"
                              : "text-silver/60 tabular-nums"
                          }
                          title="p75 of stored samples for this breakpoint"
                        >
                          {actual != null ? `p75 ${actual}ms` : "p75 —"}
                          {over ? " ⚠" : ""}
                        </span>
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

export default HeroBudgetEditor;
